// =====================================================
// IMPULSIVE TRADER BENCHMARK - TYPE DEFINITIONS
// Simulate what happens if user takes ALL trades (ignoring NO_TRADE/WAIT)
// =====================================================

import type { CounterfactualTracking } from './counterfactual';

export interface ImpulsiveTraderOutcome {
  journalEntryId: string;
  decision: 'NO_TRADE' | 'WAIT';
  directionIfTraded: 'LONG' | 'SHORT' | null;
  priceAtDecision: number;
  
  // Hypothetical trade outcome
  hypotheticalEntry: number;
  hypotheticalStopLoss: number | null;
  
  // What would have happened
  worstExcursion: number | null;
  bestExcursion: number | null;
  price24hLater: number | null;
  
  // Calculated outcome
  hypotheticalOutcome: 'WIN' | 'LOSS' | 'BREAKEVEN' | 'PENDING';
  hypotheticalRMultiple: number | null;
  hypotheticalPnlUsd: number | null;
  
  // Did the AI save capital?
  capitalSaved: boolean;
  lossAvoided: number | null;
}

export interface ImpulsiveTraderStats {
  // Core metrics
  totalNoTradeDecisions: number;
  decisionsWithTracking: number;
  
  // Impulsive trader simulation
  impulsivePnlR: number;
  impulsivePnlUsd: number;
  impulsiveWins: number;
  impulsiveLosses: number;
  impulsiveWinRate: number;
  
  // Actual trader (only real trades taken)
  actualPnlR: number;
  actualPnlUsd: number;
  
  // Discipline Premium = Actual - Impulsive (positive = discipline saved money)
  disciplinePremiumR: number;
  disciplinePremiumUsd: number;
  
  // Risk metrics
  avoidedDrawdownPercent: number;
  lossesAvoided: number;
  totalLossAvoidedUsd: number;
  
  // Badge qualification
  hasInstitutionalEdge: boolean;
}

export interface ImpulsiveTradeSimulation {
  tracking: CounterfactualTracking;
  outcome: ImpulsiveTraderOutcome;
}

// Default risk per trade for calculations
export const DEFAULT_RISK_PER_TRADE_USD = 100;

// Calculate impulsive trader outcome from counterfactual tracking
export function calculateImpulsiveOutcome(
  tracking: CounterfactualTracking,
  decision: 'NO_TRADE' | 'WAIT'
): ImpulsiveTraderOutcome {
  const {
    journalEntryId,
    priceAtDecision,
    directionIfTraded,
    hypotheticalStopLoss,
    priceAfter24h,
    maxAdverseExcursion,
    maxFavorableExcursion,
    capitalPreservedR,
    capitalPreservedUsd,
    hypotheticalRiskAmount,
  } = tracking;

  // Determine worst/best excursion based on direction
  let worstExcursion: number | null = null;
  let bestExcursion: number | null = null;

  if (directionIfTraded === 'LONG') {
    worstExcursion = maxAdverseExcursion ? -maxAdverseExcursion : null;
    bestExcursion = maxFavorableExcursion || null;
  } else if (directionIfTraded === 'SHORT') {
    worstExcursion = maxAdverseExcursion ? -maxAdverseExcursion : null;
    bestExcursion = maxFavorableExcursion || null;
  }

  // Calculate hypothetical outcome
  let hypotheticalOutcome: 'WIN' | 'LOSS' | 'BREAKEVEN' | 'PENDING' = 'PENDING';
  let hypotheticalRMultiple: number | null = null;
  let hypotheticalPnlUsd: number | null = null;
  let capitalSaved = false;
  let lossAvoided: number | null = null;

  // If we have capital preserved data, use it (inverted - positive capital preserved = loss for impulsive trader)
  if (capitalPreservedR !== null) {
    // capitalPreservedR > 0 means we saved capital by NOT trading (impulsive trader would have LOST)
    // capitalPreservedR < 0 means we missed opportunity (impulsive trader would have WON)
    hypotheticalRMultiple = -capitalPreservedR; // Impulsive trader's PnL is opposite of capital preserved
    hypotheticalPnlUsd = hypotheticalRMultiple * (hypotheticalRiskAmount || DEFAULT_RISK_PER_TRADE_USD);

    if (hypotheticalRMultiple > 0.1) {
      hypotheticalOutcome = 'WIN';
    } else if (hypotheticalRMultiple < -0.1) {
      hypotheticalOutcome = 'LOSS';
      capitalSaved = true;
      lossAvoided = Math.abs(hypotheticalPnlUsd);
    } else {
      hypotheticalOutcome = 'BREAKEVEN';
    }
  }

  return {
    journalEntryId,
    decision,
    directionIfTraded,
    priceAtDecision,
    hypotheticalEntry: priceAtDecision,
    hypotheticalStopLoss,
    worstExcursion,
    bestExcursion,
    price24hLater: priceAfter24h,
    hypotheticalOutcome,
    hypotheticalRMultiple,
    hypotheticalPnlUsd,
    capitalSaved,
    lossAvoided,
  };
}

// Format discipline premium for display
export function formatDisciplinePremium(r: number, usd: number): string {
  const rSign = r >= 0 ? '+' : '';
  const usdSign = usd >= 0 ? '+' : '';
  return `${rSign}${r.toFixed(1)}R / ${usdSign}$${Math.abs(usd).toLocaleString()}`;
}

// Get color class based on discipline premium
export function getDisciplinePremiumColor(premium: number): string {
  if (premium > 0) return 'text-emerald-400';
  if (premium < 0) return 'text-amber-400';
  return 'text-gray-400';
}

// Get badge text based on stats
export function getInstitutionalEdgeBadge(stats: ImpulsiveTraderStats): string | null {
  if (!stats.hasInstitutionalEdge) return null;
  if (stats.disciplinePremiumR >= 5) return '🏆 Elite Discipline';
  if (stats.disciplinePremiumR >= 2) return '✨ Institutional Edge';
  return '🛡️ Capital Protected';
}
